import { parsePeriod } from "./period.js";
export const EEX_QUOTE_SCHEMA_VERSION = 1;
const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];
const finite = (value) => typeof value === "number" && Number.isFinite(value);
export function eexQuoteRowIsActive(row) {
    return finite(row.mw) && row.mw !== 0;
}
export function eexQuoteRowHasMarketInputs(row) {
    return (finite(row.huPrice) &&
        finite(row.quotedSpread) &&
        finite(row.risk) &&
        finite(row.fx) &&
        row.fx > 0);
}
function sum2(a, b) {
    return finite(a) && finite(b) ? a + b : null;
}
function multiply2(a, b) {
    return finite(a) && finite(b) ? a * b : null;
}
export function eexQuoteMonthLabel(month) {
    const parsed = /^(\d{4})-(\d{2})$/.exec(month);
    if (!parsed)
        return month;
    const year = Number(parsed[1]);
    const monthIndex = Number(parsed[2]) - 1;
    const label = MONTHS[monthIndex];
    if (!label || !Number.isInteger(year))
        return month;
    return `${label}-${String(year % 100).padStart(2, "0")}`;
}
export function eexQuoteMonthHours(month) {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) {
        throw new Error(`invalid EEX quote delivery month: ${month}`);
    }
    // Parse the full four-digit year. Going through the display label would fold
    // 1900/2100 into 2000 and produce the wrong Gregorian leap-year hours.
    const parsed = parsePeriod(`${month}-01`);
    if (!parsed || parsed.kind !== "month") {
        throw new Error(`invalid EEX quote delivery month: ${month}`);
    }
    return parsed.hours;
}
export function addEexQuoteMonth(month, delta) {
    const parsed = /^(\d{4})-(\d{2})$/.exec(month);
    if (!parsed || !Number.isInteger(delta)) {
        throw new Error(`invalid EEX quote month or delta: ${month}, ${delta}`);
    }
    const date = new Date(Date.UTC(Number(parsed[1]), Number(parsed[2]) - 1 + delta, 1));
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}
export function eexQuoteMonthRange(startMonth, endMonth) {
    if (!/^\d{4}-\d{2}$/.test(startMonth) || !/^\d{4}-\d{2}$/.test(endMonth)) {
        throw new Error("quote month range must use YYYY-MM");
    }
    if (startMonth > endMonth)
        return [];
    const result = [];
    let month = startMonth;
    while (month <= endMonth) {
        result.push(month);
        if (result.length > 600)
            throw new Error("quote month range is too large");
        month = addEexQuoteMonth(month, 1);
    }
    return result;
}
export function makeEexQuoteRows(startMonth, endMonth, defaults) {
    return eexQuoteMonthRange(startMonth, endMonth).map((month) => ({
        id: month,
        month,
        hours: eexQuoteMonthHours(month),
        mw: defaults.mw ?? 0,
        huPrice: defaults.huPrice ?? null,
        quotedSpread: defaults.quotedSpread ?? null,
        risk: defaults.defaultRisk,
        fx: defaults.fx ?? null,
    }));
}
export function computeEexQuoteRow(input, settings) {
    const mwh = finite(input.hours) && finite(input.mw) ? input.hours * input.mw : null;
    const spread = sum2(input.quotedSpread, input.risk);
    const roEur = sum2(input.huPrice, spread);
    const roRon = multiply2(roEur, input.fx);
    const buyRon = finite(roRon) ? roRon + settings.ronBuyAdder : null;
    const sellRon = finite(buyRon) ? buyRon + settings.ronSellAdder : null;
    const buyEur = finite(roEur) ? roEur + settings.eurBuyAdder : null;
    const sellEur = finite(buyEur) ? buyEur + settings.eurSellAdder : null;
    return {
        ...input,
        mwh,
        spread,
        roEur,
        roRon,
        buyRon,
        sellRon,
        buyEur,
        sellEur,
    };
}
const WEIGHTED_KEYS = [
    "huPrice",
    "quotedSpread",
    "risk",
    "spread",
    "roEur",
    "fx",
    "roRon",
    "buyRon",
    "sellRon",
    "buyEur",
    "sellEur",
];
function rowWeight(row) {
    // Quote notionals are magnitudes. Treat a stray negative MW as its absolute
    // notional so weighted prices never invert or divide through zero.
    return finite(row.mwh) ? Math.abs(row.mwh) : 0;
}
function weightedAverage(rows, key) {
    let weighted = 0;
    let weight = 0;
    for (const row of rows) {
        const value = row[key];
        const w = rowWeight(row);
        if (!finite(value) || w <= 0)
            continue;
        weighted += value * w;
        weight += w;
    }
    return weight > 0 ? weighted / weight : null;
}
export function computeEexQuoteBook(inputs, settings) {
    const baseRows = inputs.map((row) => computeEexQuoteRow(row, settings));
    const totalMwh = baseRows.reduce((sum, row) => sum + rowWeight(row), 0);
    const weighted = Object.fromEntries(WEIGHTED_KEYS.map((key) => [key, weightedAverage(baseRows, key)]));
    const flatSellRon = weighted.sellRon;
    const rows = baseRows.map((row) => ({
        ...row,
        shapePnlRon: finite(flatSellRon) && finite(row.sellRon) && rowWeight(row) > 0
            ? (flatSellRon - row.sellRon) * rowWeight(row)
            : null,
    }));
    const shapePnlRon = rows.reduce((sum, row) => sum + (finite(row.shapePnlRon) ? row.shapePnlRon : 0), 0);
    const grossShapePnlRon = rows.reduce((sum, row) => sum + (finite(row.shapePnlRon) ? Math.abs(row.shapePnlRon) : 0), 0);
    return {
        rows,
        totals: {
            totalMwh,
            ...weighted,
            shapePnlRon,
            grossShapePnlRon,
        },
    };
}
//# sourceMappingURL=eex-quote.js.map